/**
 * 이상한 방 - 매일의 방 카드 (메뉴 맨 위, 순수 DOM)
 *
 * 근거 문서: 로드맵 확정안 4절 6번(매일의 방 · 연속 출석) · M9 규칙 1·2 · M13(메뉴) ·
 *           `game/daily.ts` 머리말(첫 결과만 기록 · 연속의 표) · `이상한방-상태모델-v0.1.md` 9.5절(싱글의 포지션)
 *
 * ## 왜 맨 위인가
 *
 * 메뉴가 묻는 것은 "무엇을 할 것인가" 하나다(`ui/menu.ts` 머리말). 매일의 방은 **하루에 한 번만
 * 새것**이라 그 질문의 첫 답이다 - 오늘 것을 안 했으면 그것이 곧 다음 한 판이고, 했으면 카드가
 * 물러나 "오늘 {score}점"이라는 기록 한 줄이 된다.
 *
 * M13 전에는 이 카드가 단계 선택의 맨 위에 있었다. 화면이 바뀌면서 사는 곳과 입는 CSS(`menu.css`)만
 * 옮겼고 **카드가 말하는 것도 규칙도 한 글자 안 바뀌었다** - 아래 규약 셋이 그때 그대로인 이유다.
 *
 * ## 카드가 말하는 것은 셋뿐이다
 *
 * 오늘 날짜 · 연속 며칠 · 오늘 것을 했는가. 그 밖의 것(어제 점수 · 순위 · 남은 시간)은 이 화면이
 * 답할 수 없거나(순위는 서버의 것이다) 답해도 다음 행동을 바꾸지 않는다.
 * 날짜는 사전을 거치지 않는다 - `2026-09-06`은 숫자라 번역할 것이 없고, 언어마다 다른 표기로 적으면
 * "같은 날짜 키 = 같은 방"이라는 규칙을 화면이 두 가지 말로 하게 된다.
 *
 * ## 준비물이 없다는 사실을 카드가 말한다
 *
 * 매일의 방은 같은 시드를 견주는 곳이라 준비물이 적용되지 않는다(`game/roundTuning.resolveDailyTuning`은
 * 인자 자체가 없다). 그 사실을 화면이 말하지 않으면 "돋보기를 챙겼는데 외우기가 안 길어졌다"가 버그로 읽힌다.
 *
 * ## 계산하지 않는다
 *
 * 연속 일수도 그날의 기록도 `game/daily.ts`가 정한 값이다. 여기서 "어제였는가"를 다시 판단하면
 * 규칙이 두 벌이 되고, 그중 하나가 조용히 관대해진다(그 파일 머리말의 같은 경고).
 */

import { t } from '../i18n'

import { createElement, formatNumber } from './dom'
import './menu.css'

/* --- 타입 --------------------------------------------------------------------- */

/** 오늘의 기록. `game/daily.DailyRecord`를 화면의 말로 옮긴 것이라 그 배열을 그대로 넘겨도 된다. */
export type DailyCardRecord = Readonly<{
  score: number
  foundCount: number
  /** 그날의 이상한 곳 수. 모르면 비운다 - 0으로 메우면 "0개 중 0개"라는 없던 기록이 생긴다(`LevelBest`의 같은 규약). */
  changeCount?: number | undefined
}>

export type DailyCardView = Readonly<{
  /** `YYYY-MM-DD`. 화면에 그대로 적힌다(머리말). */
  dateKey: string
  /** 연속 출석 일수. 0이면 아직 없다. */
  streak: number
  /** 오늘의 첫 결과. 아직 안 했으면 `null`. */
  record: DailyCardRecord | null
}>

export type DailyCardHandle = Readonly<{
  root: HTMLElement
  render: (view: DailyCardView) => void
  dispose: () => void
}>

/* --- 문구 --------------------------------------------------------------------- */

/**
 * 오늘 한 줄. 안 했으면 "아직", 했으면 점수다.
 *
 * 분모(그날의 이상한 곳 수)가 없으면 점수만 말한다 - `levelSelect.statusLineFor`와 **같은 결정**이고
 * 이유도 같다: 없는 값을 0으로 메우면 그건 정보가 아니라 거짓 기록이다.
 */
export function dailyStatusLine(view: DailyCardView): string {
  const record = view.record
  if (record === null) return t('daily.todayNone')
  if (record.changeCount === undefined) return t('daily.todayDoneScore', { score: formatNumber(record.score) })
  return t('daily.todayDone', {
    score: formatNumber(record.score),
    found: record.foundCount,
    total: record.changeCount,
  })
}

/** 연속 한 줄. 0일 때 "연속 0일"이라 적지 않는 이유는 그것이 기록이 아니라 없음이기 때문이다. */
export function dailyStreakLine(streak: number): string {
  return streak > 0 ? t('daily.streak', { count: streak }) : t('daily.streakNone')
}

/* --- 본체 --------------------------------------------------------------------- */

/**
 * 매일의 방 카드를 만든다. **붙이는 것은 부르는 쪽의 몫이다**(`root`를 내준다 - `rankingList`·`pinsChip`과 같은 결정).
 *
 * 카드 전체가 버튼 하나다. 읽을 것은 제목·칩 둘·한 줄이고 누를 것은 하나뿐이라, 안에 작은 버튼을 또 두면
 * 눌리는 면적만 줄어든다(여럿이 카드와 같은 구조).
 *
 * 문구를 `render`에서 굽는 이유는 이 카드가 매번 다시 그려지기 때문이다 - 선택 화면이 언어 전환에
 * 통째로 다시 서므로(`main.rebuildOverlays`) 굳은 문구가 남을 자리가 없다.
 */
export function createDailyCard(onStart: () => void): DailyCardHandle {
  const root = createElement('section', 'mn-daily')

  const card = createElement('button', 'mn-card')
  card.type = 'button'

  const eyebrow = createElement('span', 'mn-card__eyebrow', '')
  eyebrow.setAttribute('aria-hidden', 'true')

  const body = createElement('span', 'mn-card__body')
  body.setAttribute('aria-hidden', 'true')
  const title = createElement('span', 'mn-card__title', '')
  const meta = createElement('span', 'mn-card__meta')
  const dateChip = createElement('span', 'mn-chip', '')
  const streakChip = createElement('span', 'mn-chip', '')
  meta.append(dateChip, streakChip)
  const status = createElement('span', 'mn-card__status', '')
  const note = createElement('span', 'mn-card__note', '')
  body.append(title, meta, status, note)

  const go = createElement('span', 'mn-card__go', '')
  go.setAttribute('aria-hidden', 'true')

  card.append(eyebrow, body, go)
  card.addEventListener('click', onStart)
  root.append(card)

  return {
    root,

    render(view: DailyCardView): void {
      const statusLine = dailyStatusLine(view)

      eyebrow.textContent = t('daily.eyebrow')
      title.textContent = t('daily.title')
      dateChip.textContent = view.dateKey
      streakChip.textContent = dailyStreakLine(view.streak)
      status.textContent = statusLine
      /*
       * 아직 안 했으면 이 방이 무엇인지 한 줄로 말하고(같은 방 · 준비물 없음 · 첫 판만 기록),
       * 끝냈으면 그 셋 중 지금 유효한 하나만 남긴다 - 다음 판은 연습이다.
       * 두 줄을 동시에 세우지 않는 이유는 카드가 목록 맨 위에 있어서다. 여기서 한 줄이 늘면 단계 하나가 화면 밖으로 나간다.
       */
      note.textContent = view.record === null ? t('daily.desc') : t('daily.practice')
      go.textContent = view.record === null ? t('daily.start') : t('daily.again')
      card.setAttribute('aria-label', t('daily.cardAria', { status: statusLine }))
    },

    dispose(): void {
      card.removeEventListener('click', onStart)
      root.remove()
    },
  }
}
